import { T } from "services/lang/LanguageService";
import {
  DATE_PATTERN_MINUTES,
  DATE_PATTERN_NO_TIME,
  DATE_PATTERN_MONTH_DAY,
} from "utils/constants";
import { DateTimeFormatter, Instant, IsoFields, ZonedDateTime, ZoneId } from "@js-joda/core";
import { toZonedDateTime } from "utils/time-utils";
import { monthsArrayShort } from "../charts/utils";
import { AggregationPeriod, DataDisplayType } from "./ValueChangeIndicatorTypes";

export const getMainClassName = (
  dataDisplayType: DataDisplayType,
  reportMode?: boolean,
  displayInline?: boolean
) => {
  const currentClassName = `last-value-indicator${reportMode ? "-report-mode" : ""}`;

  if (dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART) {
    return `${currentClassName}-horizontal`;
  }
  if (displayInline) {
    return `${currentClassName}-inline`;
  }
  return currentClassName;
};

export const getTitleClassName = (
  dataDisplayType: DataDisplayType,
  displayInline?: boolean
): string => {
  const baseTitle =
    dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART ? "title-horizontal" : "title";

  const fontWeight =
    displayInline || dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART
      ? "font-bold"
      : "font-medium";

  return `${baseTitle} ${fontWeight}`;
};

export const getDiffClassName = (
  isNeutral: boolean,
  dataDisplayType: DataDisplayType,
  useDiffIndicatorColors?: boolean
): string => {
  const baseDiff =
    dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART
      ? "last-value-diff-horizontal"
      : "last-value-diff";

  const classNames = [
    baseDiff,
    "flex flex-row align-items-center justify-content-end",
    useDiffIndicatorColors && !isNeutral && "indicator-colors",
    isNeutral && "neutral",
  ]
    .filter(Boolean)
    .join(" ");

  return classNames;
};

export const formatGroupKey = (date: Date, period: AggregationPeriod): string => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  switch (period) {
    case AggregationPeriod.MINUTES:
      return `${year}-${month + 1}-${day} ${date.getHours()}:${date.getMinutes()}`;
    case AggregationPeriod.HOURS:
      return `${year}-${month + 1}-${day} ${date.getHours()}:00`;
    case AggregationPeriod.DAYS:
      return `${year}-${month + 1}-${day}`;
    case AggregationPeriod.WEEKS: {
      const d = new Date(date);
      const dayOfWeek = d.getDay();
      const diff = d.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      const monday = new Date(d.setDate(diff));
      return `${monday.getFullYear()}-${monthsArrayShort[monday.getMonth()]}-${monday.getDate()} - ${T("word.week")}`;
    }
    case AggregationPeriod.MONTHS:
      return `${monthsArrayShort[month]} ${year}`;
    case AggregationPeriod.YEARS:
      return `${year} ${T("word.year-one-letter")}.`;
    default:
      return "";
  }
};

export const fmt = (pattern: string) => DateTimeFormatter.ofPattern(pattern);

export const getUpToPeriodLabel = (
  upToUnit: AggregationPeriod,
  referenceTime: Instant | undefined,
  zoneId: ZoneId,
) => {
  const currentAlignUnit = (upToUnit ?? AggregationPeriod.MONTHS).toString().toUpperCase();
  const now: ZonedDateTime = toZonedDateTime(referenceTime || Instant.now(), zoneId);

  switch (currentAlignUnit) {
    case AggregationPeriod.MINUTES:
      return now.format(fmt("HH:mm"));
    case AggregationPeriod.HOURS:
      return now.format(fmt("HH"));
    case AggregationPeriod.DAYS:
      return now.format(fmt(DATE_PATTERN_NO_TIME));
    case AggregationPeriod.WEEKS: {
      const week = now.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
      return `W${week}`;
    }
    case AggregationPeriod.MONTHS: {
      const lastDayOfMonth = now.withDayOfMonth(1).minusDays(1);
      return lastDayOfMonth.format(fmt(DATE_PATTERN_MONTH_DAY));
    }
    case AggregationPeriod.YEARS:
      return String(now.year());
    default:
      return now.format(fmt(DATE_PATTERN_MINUTES));
  }
};

export const getMonthYearComparison = (
  dataset: { key: string; value: number }[],
  isLastFinishedMonth: boolean,
  zone: ZoneId,
) => {
  const now = ZonedDateTime.now(zone);

  const firstDayOfCurrentMonth = now
    .withDayOfMonth(1)
    .withHour(0)
    .withMinute(0)
    .withSecond(0)
    .withNano(0);

  let lastFinished: ZonedDateTime;

  if (isLastFinishedMonth) {
    lastFinished = firstDayOfCurrentMonth.minusNanos(1);
  } else {
    lastFinished = firstDayOfCurrentMonth;
  }

  const targetYear = lastFinished.year();
  const targetMonth = lastFinished.monthValue();

  const findValue = (y: number, m: number) => {
    return (
      dataset.find(d => {
        const dt = ZonedDateTime.parse(d.key).withZoneSameInstant(zone);
        return dt.year() === y && dt.monthValue() === m;
      })?.value ?? null
    );
  };

  const curr = findValue(targetYear, targetMonth);
  const prev = findValue(targetYear - 1, targetMonth);
  const currValueKey = `${targetYear}-${targetMonth}`;
  const prevValueKey = `${targetYear - 1}-${targetMonth}`;

  return { curr, prev, currValueKey, prevValueKey };
};
